import { retry } from "@reduxjs/toolkit/query/react";
import type {
  OrderDetailSerializer,
  IApiResponse,
  CreateOrderSerializer,
  OrderAnalyticsInfoSerializer,
  GACookieSerializer,
} from "@litres-libs/types";

import { rtkqApi } from "../api/rtkqApi";
import { axiosBaseQuery } from "../api/baseQuery";
import { standardRetryingRequestOptions } from "../api/retrying";
import { getBaseAPIUrl } from "../../request/config/api";
import type { RootState } from "../../store";
import { TagsType } from "../constants";

enum HostsWhichHavePdaVersion {
  "www.litres.ru" = "www.litres.ru",
  "litres.com" = "litres.com",
  "litres-tr.com" = "litres-tr.com",
  "litres.az" = "litres.az",
  "litres.ee" = "litres.ee",
  "litres.pl" = "litres.pl",
  "litres.de" = "litres.de",
  "litres.es" = "litres.es",
  "litres.ua" = "litres.ua",
  "litres.uz" = "litres.uz",
  "litres.eu" = "litres.eu",
}

export const ordersApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getOrder: build.query<OrderDetailSerializer, number>({
      query: orderId => ({
        url: `/foundation/api/orders/${orderId}`,
      }),
      transformResponse: (response: IApiResponse<OrderDetailSerializer>) =>
        response.payload.data,
      extraOptions: {
        ...standardRetryingRequestOptions,
      },
    }),
    createOrder: build.mutation<OrderDetailSerializer, CreateOrderSerializer>({
      queryFn: async (params, api) => {
        const makeQuery = retry(axiosBaseQuery({ baseUrl: getBaseAPIUrl() }), {
          ...standardRetryingRequestOptions,
        });
        const isPDA = (api.getState() as RootState).browser.pda;
        const realClientHost = (api.getState() as RootState).browser.clientHost;

        /**
         * На бэкенде есть логика, которая ожидает, что при запросе на создание заказа в качестве заголовка client-host будет приходить www.litres.ru для пк/ноутбука и pda.litres.ru для планшетов/мобильных экранов. Логика странная, но от нее зависит аналитика, которая строит дашборды для web/pda в зависимости от указанного хоста.
         * Подобная логика валидна и для иностранных доменов.
         * При этом не для всех хостов в Литрес существует PDA-версия, так что мы меняем хост только для тех, для которых существует.
         */
        let clientHostForSending = realClientHost;

        if (
          isPDA &&
          (Object.values(HostsWhichHavePdaVersion) as string[]).includes(
            realClientHost
          )
        ) {
          if (realClientHost === HostsWhichHavePdaVersion["www.litres.ru"]) {
            clientHostForSending = "pda.litres.ru";
          } else if (realClientHost === HostsWhichHavePdaVersion["litres.ua"]) {
            clientHostForSending = "pda.litres.ua";
          } else {
            clientHostForSending = "m." + realClientHost;
          }
        }

        const response = await makeQuery(
          {
            url: "/foundation/api/orders",
            method: "POST",
            body: {
              ...params,
            },
            headers: {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              "client-host": clientHostForSending,
            },
          },
          api,
          {}
        );

        if (response.error) {
          return {
            error: response.error,
          };
        }

        return {
          ...(response.data as IApiResponse<OrderDetailSerializer>).payload,
        };
      },
    }),
    getOrderAnalytics: build.query<OrderAnalyticsInfoSerializer, number>({
      query: orderId => ({
        url: `/foundation/api/orders/${orderId}/analytics`,
      }),
      transformResponse: (
        response: IApiResponse<OrderAnalyticsInfoSerializer>
      ) => response.payload.data,
      extraOptions: {
        ...standardRetryingRequestOptions,
      },
    }),
    updateGAextendesCookies: build.query<GACookieSerializer, void>({
      query: () => ({
        url: "/foundation/api/internal/transferred-cookies",
        method: "POST",
      }),
      transformResponse: (response: IApiResponse<GACookieSerializer>) =>
        response.payload.data,
      providesTags: () => [TagsType.GA_EXTENDS],
    }),
  }),
});
